/* ======================================================= les tests exacts */
/**
 * Le segment [a, b] traverse-t-il la boîte ? Découpage paramétrique
 * (Liang-Barsky). Mêmes opérations, dans le même ordre, que la version à
 * tableau qu'il remplace : le même résultat au bit près, sans allocation.
 */
export function segmentTraverse(a, b, r) {
    let t0 = 0;
    let t1 = 1;
    const dx = b.x - a.x;
    const dy = b.y - a.y;
    let p = -dx;
    let q = a.x - r.x;
    if (Math.abs(p) < 1e-12) {
        if (q < 0)
            return false;
    }
    else if (p < 0)
        t0 = Math.max(t0, q / p);
    else
        t1 = Math.min(t1, q / p);
    p = dx;
    q = r.x + r.largeur - a.x;
    if (Math.abs(p) < 1e-12) {
        if (q < 0)
            return false;
    }
    else if (p < 0)
        t0 = Math.max(t0, q / p);
    else
        t1 = Math.min(t1, q / p);
    p = -dy;
    q = a.y - r.y;
    if (Math.abs(p) < 1e-12) {
        if (q < 0)
            return false;
    }
    else if (p < 0)
        t0 = Math.max(t0, q / p);
    else
        t1 = Math.min(t1, q / p);
    p = dy;
    q = r.y + r.hauteur - a.y;
    if (Math.abs(p) < 1e-12) {
        if (q < 0)
            return false;
    }
    else if (p < 0)
        t0 = Math.max(t0, q / p);
    else
        t1 = Math.min(t1, q / p);
    return t0 < t1;
}
/** Deux boîtes se chevauchent-elles ? Un bord commun ne compte pas. */
export const chevauchent = (p, q) => p.x < q.x + q.largeur && q.x < p.x + p.largeur && p.y < q.y + q.hauteur && q.y < p.y + p.hauteur;
/** La boîte qui compte pour une collision : un effleurement de 0,5 ne compte pas. */
export const boiteDeCollision = (b) => ({
    x: b.x + 0.5,
    y: b.y + 0.5,
    largeur: b.largeur - 1,
    hauteur: b.hauteur - 1,
});
/* ================================================================ l'index */
/**
 * Tout ce qui passe à moins de `MARGE` d'une cellule y est rangé, et une
 * requête lit les cellules à moins de `MARGE` de sa boîte : les arrondis du
 * calcul (1e-13 d'une unité d'affichage) ne peuvent pas faire manquer un
 * tracé.
 */
const MARGE = 1;
/** Le côté d'une cellule, en unités d'affichage : un peu plus qu'un nom (13 × 8,5). */
const CELLULE = 16;
/** Au plus 64 × 64 cellules ; au-delà, les cellules grandissent. */
const CELLULES_MAX = 64;
/**
 * La grille des collisions d'un dessin. On y range les tracés (`obstacles`) et
 * les boîtes (textes, disques des points) au fur et à mesure qu'ils sont
 * posés ; `compter` rend ce que rendrait `collisionsV2` sur tout ce qui a été
 * rangé.
 *
 * La grille couvre `cadre` (la fenêtre du repère, en unités d'affichage) ; ce
 * qui déborde tombe dans les cellules du bord, qui s'étendent à l'infini.
 */
export class IndexCollisions {
    ox;
    oy;
    cote;
    nx;
    ny;
    cellulesTraces;
    cellulesBoites;
    traces = [];
    boites = [];
    vuTrace = [];
    vuBoite = [];
    tour = 0;
    constructor(cadre) {
        const l = Math.max(cadre.largeur, 1);
        const h = Math.max(cadre.hauteur, 1);
        this.cote = Math.max(CELLULE, l / CELLULES_MAX, h / CELLULES_MAX);
        this.ox = cadre.x;
        this.oy = cadre.y;
        this.nx = Math.max(1, Math.ceil(l / this.cote));
        this.ny = Math.max(1, Math.ceil(h / this.cote));
        this.cellulesTraces = new Array(this.nx * this.ny);
        this.cellulesBoites = new Array(this.nx * this.ny);
    }
    colonne(x) {
        const i = Math.floor((x - this.ox) / this.cote);
        return i < 0 ? 0 : i >= this.nx ? this.nx - 1 : i;
    }
    ligne(y) {
        const i = Math.floor((y - this.oy) / this.cote);
        return i < 0 ? 0 : i >= this.ny ? this.ny - 1 : i;
    }
    static ranger(cellules, k, id) {
        const c = cellules[k];
        if (c === undefined)
            cellules[k] = [id];
        else if (c[c.length - 1] !== id)
            c.push(id);
    }
    /**
     * Range un tracé. Colonne par colonne, seulement les lignes de cellules que
     * le segment traverse dans cette colonne (marge comprise) : une droite
     * oblique occupe une bande de cellules, pas tout son rectangle englobant.
     */
    ajouterTrace(a, b) {
        const id = this.traces.length;
        this.traces.push([a, b]);
        this.vuTrace.push(0);
        const xlo = Math.min(a.x, b.x) - MARGE;
        const xhi = Math.max(a.x, b.x) + MARGE;
        const c0 = this.colonne(xlo);
        const c1 = this.colonne(xhi);
        const dx = b.x - a.x;
        const dy = b.y - a.y;
        const entier = c0 === c1 || Math.abs(dx) < 1e-9;
        for (let c = c0; c <= c1; c += 1) {
            let ylo;
            let yhi;
            if (entier) {
                ylo = Math.min(a.y, b.y);
                yhi = Math.max(a.y, b.y);
            }
            else {
                const g = c === 0 ? -Infinity : this.ox + c * this.cote;
                const d = c === this.nx - 1 ? Infinity : this.ox + (c + 1) * this.cote;
                const t0 = Math.min(1, Math.max(0, (Math.max(xlo, g) - a.x) / dx));
                const t1 = Math.min(1, Math.max(0, (Math.min(xhi, d) - a.x) / dx));
                const y0 = a.y + t0 * dy;
                const y1 = a.y + t1 * dy;
                ylo = Math.min(y0, y1);
                yhi = Math.max(y0, y1);
            }
            const l1 = this.ligne(yhi + MARGE);
            for (let l = this.ligne(ylo - MARGE); l <= l1; l += 1) {
                IndexCollisions.ranger(this.cellulesTraces, l * this.nx + c, id);
            }
        }
    }
    ajouterBoite(b) {
        const id = this.boites.length;
        this.boites.push(b);
        this.vuBoite.push(0);
        const c1 = this.colonne(Math.max(b.x, b.x + b.largeur) + MARGE);
        const l1 = this.ligne(Math.max(b.y, b.y + b.hauteur) + MARGE);
        const c0 = this.colonne(Math.min(b.x, b.x + b.largeur) - MARGE);
        for (let l = this.ligne(Math.min(b.y, b.y + b.hauteur) - MARGE); l <= l1; l += 1) {
            for (let c = c0; c <= c1; c += 1) {
                IndexCollisions.ranger(this.cellulesBoites, l * this.nx + c, id);
            }
        }
    }
    /**
     * Les tracés, puis les boîtes, rangés dans les cellules à moins de `MARGE`
     * du rectangle [x0, x1] × [y0, y1] : chacun une fois.
     */
    parcourir(x0, y0, x1, y1, surTrace, surBoite) {
        this.tour += 1;
        const tour = this.tour;
        const c0 = this.colonne(x0 - MARGE);
        const c1 = this.colonne(x1 + MARGE);
        const l0 = this.ligne(y0 - MARGE);
        const l1 = this.ligne(y1 + MARGE);
        if (surTrace !== null) {
            for (let l = l0; l <= l1; l += 1) {
                for (let c = c0; c <= c1; c += 1) {
                    const ids = this.cellulesTraces[l * this.nx + c];
                    if (ids === undefined)
                        continue;
                    for (const id of ids) {
                        if (this.vuTrace[id] === tour)
                            continue;
                        this.vuTrace[id] = tour;
                        if (surTrace(id))
                            return;
                    }
                }
            }
        }
        if (surBoite !== null) {
            for (let l = l0; l <= l1; l += 1) {
                for (let c = c0; c <= c1; c += 1) {
                    const ids = this.cellulesBoites[l * this.nx + c];
                    if (ids === undefined)
                        continue;
                    for (const id of ids) {
                        if (this.vuBoite[id] === tour)
                            continue;
                        this.vuBoite[id] = tour;
                        if (surBoite(id))
                            return;
                    }
                }
            }
        }
    }
    /**
     * Combien de tracés et de boîtes la boîte `b` heurte — le compte exact de
     * `collisionsV2` sur tout ce qui a été rangé. Le compte s'arrête à
     * `plafond` : au-delà, l'appelant sait déjà que la position perd.
     */
    compter(b, plafond = Infinity) {
        if (plafond <= 0)
            return 0;
        const r = boiteDeCollision(b);
        let n = 0;
        const x0 = Math.min(r.x, r.x + r.largeur);
        const y0 = Math.min(r.y, r.y + r.hauteur);
        const x1 = Math.max(r.x, r.x + r.largeur);
        const y1 = Math.max(r.y, r.y + r.hauteur);
        this.parcourir(x0, y0, x1, y1, (id) => {
            const [a, z] = this.traces[id];
            if (segmentTraverse(a, z, r))
                n += 1;
            return n >= plafond;
        }, (id) => {
            if (n >= plafond)
                return true;
            if (chevauchent(r, this.boites[id]))
                n += 1;
            return n >= plafond;
        });
        return n;
    }
    /** Chaque tracé rangé à moins de `rayon` (au sens du carré) de `p`, et d'autres plus loin. */
    tracesPres(p, rayon, f) {
        this.parcourir(p.x - rayon, p.y - rayon, p.x + rayon, p.y + rayon, (id) => {
            const [a, b] = this.traces[id];
            f(a, b);
            return false;
        }, null);
    }
    /** Chaque boîte rangée à moins de `rayon` (au sens du carré) de `p`, et d'autres plus loin. */
    boitesPres(p, rayon, f) {
        this.parcourir(p.x - rayon, p.y - rayon, p.x + rayon, p.y + rayon, null, (id) => {
            f(this.boites[id]);
            return false;
        });
    }
}
/**
 * Le centre figé d'un nom : à sa place si son point n'a pas bougé (le même
 * nombre, pas un recalcul), translaté avec lui sinon.
 */
export function centreNomFige(n, p) {
    if (n.point.x === p.x && n.point.y === p.y)
        return n.centre;
    return { x: n.centre.x + (p.x - n.point.x), y: n.centre.y + (p.y - n.point.y) };
}
//# sourceMappingURL=placement-etiquettes.js.map